import { create } from 'zustand'
import { useAppPreferencesStore } from './useAppPreferencesStore'

export interface CurrencyData {
  code: string
  name: string
  icon?: string
  sendRequestBGRefColor: string | string[]
  sendRequestStartGradient?: [number, number]
  sendRequestEndGradient?: [number, number]
  avatarBGRefColor: string | string[]
}

export interface CryptoAmount {
  amount: string
  code: string
}

export interface FiatAmount {
  amount: string
  code: string
}

export interface Recipient {
  id?: string
  name: string
  phone?: string
}

export interface SendRequestForm {
  onRampTotalAmount: FiatAmount
  recipient: Recipient
  note: string
}

export type PaymentMethod =
  | 'Wallet'
  | 'Card'
  | 'ApplePay'
  | 'GooglePay'
  | 'OnlineBanking'

type SendType = 'send' | 'request'

interface SendState {
  // Core state
  type: SendType
  isFiatMode: boolean
  triggerShake: boolean

  // Amounts
  cryptoAmount: CryptoAmount
  fiatAmount: FiatAmount

  // Currency
  selectedCurrency?: CurrencyData

  // Recipient
  recipient?: Recipient

  // Note
  note: string

  // Payment method
  paymentMethod: PaymentMethod

  // Send request form (for on-ramp data)
  sendRequestForm: SendRequestForm

  // UI state
  isLoading: boolean
  error?: string | null
  isCurrencyModalOpen: boolean
}

interface SendActions {
  // Core actions
  setType: (type: SendType) => void
  setIsFiatMode: (isFiatMode: boolean) => void

  // Amount management
  setCryptoAmount: (amount: CryptoAmount) => void
  setFiatAmount: (amount: FiatAmount) => void

  // Currency
  setSelectedCurrency: (currency: CurrencyData) => void

  // Recipient
  setRecipient: (recipient: Recipient) => void

  // Note
  setNote: (note: string) => void

  // Payment method
  setPaymentMethod: (method: PaymentMethod) => void

  // Send request form
  setSendRequestForm: (form: Partial<SendRequestForm>) => void

  // UI actions
  setIsLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  shakeComponent: () => void
  setIsCurrencyModalOpen: (open: boolean) => void

  // Reset
  resetSend: () => void
}

// Get currency colors (same as RN version)
const getCurrencyColors = (currencyCode: string) => {
  const code = currencyCode.toLowerCase()

  switch (code) {
    case 'usdt':
    case 'usd':
      return {
        sendRequestBGRefColor: '#00CF46',
        avatarBGRefColor: '#33CC67',
      }

    case 'btc':
      return {
        sendRequestBGRefColor: '#F7931A',
        avatarBGRefColor: '#F7931A',
      }

    case 'eth':
      return {
        sendRequestBGRefColor: '#7059FF',
        avatarBGRefColor: '#7059FF',
      }

    case 'xaut':
      return {
        sendRequestBGRefColor: ['#F7D14E', '#D4A041'],
        sendRequestStartGradient: [0, 0] as [number, number],
        sendRequestEndGradient: [1, 0] as [number, number],
        avatarBGRefColor: ['#FCFBE7', '#FFD475', '#DD8536'],
      }

    case 'sol':
      return {
        sendRequestBGRefColor: ['#41FFD1', '#534FFF'],
        sendRequestStartGradient: [0.7, 0] as [number, number],
        sendRequestEndGradient: [0.9, 1] as [number, number],
        avatarBGRefColor: '#000000',
      }

    case 'kwdk':
      return {
        sendRequestBGRefColor: '#0051A0',
        avatarBGRefColor: '#0051A0',
      }

    case 'aedk':
      return {
        sendRequestBGRefColor: '#000000',
        avatarBGRefColor: '#000000',
      }

    case 'sark':
      return {
        sendRequestBGRefColor: '#005430',
        avatarBGRefColor: '#005430',
      }

    default:
      return {
        sendRequestBGRefColor: '#5F6163',
        avatarBGRefColor: '#5F6163',
      }
  }
}

// Initial state
const initialState: SendState = {
  type: 'send',
  isFiatMode: true,
  triggerShake: false,
  cryptoAmount: {
    amount: '0',
    code: 'USDT',
  },
  fiatAmount: {
    amount: '0',
    code: 'USD', // Will be updated on displayCurrency initialization
  },
  selectedCurrency: {
    code: 'USDT',
    name: 'Tether USD',
    ...getCurrencyColors('USDT'),
  },
  note: '',
  paymentMethod: 'Wallet',
  sendRequestForm: {
    onRampTotalAmount: {
      amount: '0',
      code: 'USD', // Will be updated on displayCurrency initialization
    },
    recipient: {
      id: undefined,
      name: '',
      phone: undefined,
    },
    note: '',
  },
  isLoading: false,
  error: null,
  isCurrencyModalOpen: false,
}

export const useSendStore = create<SendState & SendActions>()((set) => ({
  ...initialState,

  // Core actions
  setType: (type) => set({ type }),
  setIsFiatMode: (isFiatMode) => set({ isFiatMode }),

  // Amount management
  setCryptoAmount: (cryptoAmount) => set({ cryptoAmount }),
  setFiatAmount: (fiatAmount) => set({ fiatAmount }),

  // Currency
  setSelectedCurrency: (currency) => set({ selectedCurrency: currency }),

  // Recipient
  setRecipient: (recipient) => set({ recipient }),

  // Note
  setNote: (note) => set({ note }),

  // Payment method
  setPaymentMethod: (paymentMethod) => set({ paymentMethod }),

  // Send request form
  setSendRequestForm: (sendRequestForm) =>
    set((state) => ({
      sendRequestForm: {
        ...state.sendRequestForm,
        ...sendRequestForm,
      },
    })),

  // UI actions
  setIsLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  shakeComponent: () => {
    set({ triggerShake: true })
    setTimeout(() => set({ triggerShake: false }), 600)
  },
  setIsCurrencyModalOpen: (isCurrencyModalOpen) => set({ isCurrencyModalOpen }),

  // Reset
  resetSend: () => {
    const displayCurrency = useAppPreferencesStore.getState().displayCurrency
    set({
      ...initialState,
      fiatAmount: {
        amount: '0',
        code: displayCurrency?.isin || 'USD',
      },
      sendRequestForm: {
        ...initialState.sendRequestForm,
        onRampTotalAmount: {
          amount: '0',
          code: displayCurrency?.isin || 'USD',
        },
      },
    })
  },
}))

// Export currency colors utility function
export { getCurrencyColors }
